import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { CloseButton } from '../close-button/close-button';
import { ToastAction } from './toast-action';
import { ToastBody } from './toast-body';
import { ToastClose } from './toast-close';
import { ToastIcon } from './toast-icon';
import { ToastRoot } from './toast-root';
import type { ToastPropsInterface } from './types/toast.types';

/**
 * Toasts use the `ToastProvider` component to
 * manage the state of all toasts  within the application. _Always_ use toasts with
 * `ToastProvider`, _never_ embed a toast directly into the content of a page. For inline
 * status messages, you can use the `Alert` or `InlineFeedback` components.
 *
 * @related ['Alert', 'Banner', 'InlineFeedback']
 * @subcomponents ['ToastProvider']
 * @since 7.4.0
 */
export const Toast = forwardRef<HTMLDivElement, ToastPropsInterface>(
  (props: ToastPropsInterface, forwardedRef) => {
    const { actionButton, children, closeButton = <CloseButton onImpact />, ...rest } = props;

    return (
      <ToastRoot ref={forwardedRef} {...rest}>
        <ToastBody>{children}</ToastBody>
        {actionButton ? <ToastAction>{actionButton}</ToastAction> : null}
        {closeButton ? <ToastClose>{closeButton}</ToastClose> : null}
      </ToastRoot>
    );
  },
) as ForwardRefExoticComponent<ToastPropsInterface> & {
  Action: typeof ToastAction;
  Body: typeof ToastBody;
  Close: typeof ToastClose;
  Icon: typeof ToastIcon;
  Root: typeof ToastRoot;
};

Toast.Action = ToastAction;
Toast.Body = ToastBody;
Toast.Close = ToastClose;
Toast.Icon = ToastIcon;
Toast.Root = ToastRoot;

Toast.displayName = 'Toast';
